import '@mantine/core/styles.css';
import '@mantine/charts/styles.css';
import '@mantine/notifications/styles.css';
import './main.css';

import React from 'react';
import ReactDOM from 'react-dom/client';
import { MantineProvider } from '@mantine/core';
import { Notifications } from '@mantine/notifications';
import { App } from './App';
import { theme } from './theme';

/* ─────────────────────────────────────────────────────────────
   Stale-chunk recovery. After a deploy, the cached index.html
   may reference chunk hashes that no longer exist on the CDN.
   Detect that error and force a fresh load (once per session).
   ───────────────────────────────────────────────────────────── */
function isChunkError(msg?: string) {
  return !!msg && /Failed to fetch dynamically imported module|Importing a module script failed|ChunkLoadError|Loading chunk \d+ failed/i.test(msg);
}
const RELOAD_KEY = '__admin_chunk_reloaded';
function recoverFromStaleChunk(err: unknown) {
  const msg = (err as any)?.message || String(err);
  if (!isChunkError(msg)) return;
  if (sessionStorage.getItem(RELOAD_KEY)) return; // already tried this session
  sessionStorage.setItem(RELOAD_KEY, '1');
  // Bust cache and reload
  location.reload();
}
window.addEventListener('error', e => recoverFromStaleChunk(e.error || e.message));
window.addEventListener('unhandledrejection', e => recoverFromStaleChunk(e.reason));

/* ─────────────────────────────────────────────────────────────
   Stale-deploy detection. We embed BUILD_ID at build time and
   poll /version.txt; if the deployed version drifts from ours,
   force a reload so the user always sees the latest UI.
   ───────────────────────────────────────────────────────────── */
declare const __BUILD_ID__: string;
const MY_BUILD_ID = typeof __BUILD_ID__ !== 'undefined' ? __BUILD_ID__ : '';
const VERSION_RELOAD_KEY = '__admin_version_reloaded';

async function checkForNewerDeploy() {
  if (!MY_BUILD_ID) return;
  try {
    const r = await fetch(`/version.txt?t=${Date.now()}`, { cache: 'no-store' });
    if (!r.ok) return;
    const deployed = (await r.text()).trim();
    if (deployed && deployed !== MY_BUILD_ID) {
      if (sessionStorage.getItem(VERSION_RELOAD_KEY) === deployed) return; // avoid loop
      sessionStorage.setItem(VERSION_RELOAD_KEY, deployed);
      console.info(`[admin] Newer deploy detected (${deployed} vs ${MY_BUILD_ID}) — reloading`);
      location.reload();
    }
  } catch { /* offline / network blip — ignore */ }
}
// Check on visibility change, every window focus, and every 15s while open
document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') checkForNewerDeploy(); });
window.addEventListener('focus', checkForNewerDeploy);
setInterval(checkForNewerDeploy, 15_000);
// First check immediately on load
setTimeout(checkForNewerDeploy, 500);

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <MantineProvider theme={theme} defaultColorScheme="light">
      <Notifications position="top-right" />
      <App />
    </MantineProvider>
  </React.StrictMode>
);
